import { cn } from '@/lib/utils'
import { COLORS, type ColorKey } from './svg-primitives'

/**
 * The shape and colour vocabulary every architecture diagram shares. One
 * component, shown in full by the ```legend block and compact under any
 * diagram from its legend toggle (z8nrz7f3kc).
 */

const SHAPES: { label: string; shape: 'rounded' | 'cylinder' | 'pill' | 'hex' | 'sharp' }[] = [
  { label: 'Application service', shape: 'rounded' },
  { label: 'PostgreSQL (RDS)', shape: 'cylinder' },
  { label: 'Load balancer', shape: 'pill' },
  { label: 'Network boundary — VPN tunnel or VPC peering', shape: 'hex' },
  { label: 'External actor — partner system or CI/CD', shape: 'sharp' },
]

const SWATCHES: { label: string; color: ColorKey }[] = [
  { label: 'Frontend', color: 'frontend' },
  { label: 'API', color: 'api' },
  { label: 'Mock sub-service', color: 'mock' },
  { label: 'MLLP Data Layer', color: 'mllp' },
  { label: 'Database', color: 'db' },
  { label: 'Network edges & tunnels', color: 'network' },
  { label: 'Auth (Cognito)', color: 'auth' },
  { label: 'MedStar systems', color: 'partner' },
  { label: 'CI/CD', color: 'ci' },
]

function ShapeIcon({ shape }: { shape: (typeof SHAPES)[number]['shape'] }) {
  const s = { fill: '#f1f5f9', stroke: '#64748b', strokeWidth: 1.5 }
  return (
    <svg viewBox='0 0 32 20' className='h-4 w-7 shrink-0' aria-hidden='true'>
      {shape === 'rounded' && <rect x={2} y={3} width={28} height={14} rx={4} {...s} />}
      {shape === 'sharp' && <rect x={2} y={3} width={28} height={14} {...s} />}
      {shape === 'pill' && <rect x={2} y={3} width={28} height={14} rx={7} {...s} />}
      {shape === 'hex' && <polygon points='7,3 25,3 30,10 25,17 7,17 2,10' {...s} />}
      {shape === 'cylinder' && (
        <g {...s}>
          <path d='M6 5 Q6 2,16 2 Q26 2,26 5 L26 15 Q26 18,16 18 Q6 18,6 15Z' />
          <path d='M6 5 Q6 8,16 8 Q26 8,26 5' fill='none' />
        </g>
      )}
    </svg>
  )
}

export function DiagramLegend({ compact }: { compact?: boolean }) {
  return (
    <div className={cn('grid gap-4 text-xs', compact ? 'sm:grid-cols-2' : 'my-6 rounded-xl border bg-card p-4 sm:grid-cols-2')}>
      <div>
        <div className='mb-2 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground'>Shape</div>
        <ul className='!mb-0 space-y-1.5 !pl-0 !list-none'>
          {SHAPES.map(s => (
            <li key={s.shape} className='!mb-0 flex items-center gap-2 text-foreground'>
              <ShapeIcon shape={s.shape} />
              {s.label}
            </li>
          ))}
        </ul>
      </div>
      <div>
        <div className='mb-2 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground'>Colour</div>
        <ul className='!mb-0 grid grid-cols-2 gap-x-3 gap-y-1.5 !pl-0 !list-none'>
          {SWATCHES.map(s => (
            <li key={s.color} className='!mb-0 flex items-center gap-2 text-foreground'>
              <span
                className='h-3 w-3 shrink-0 rounded-sm border'
                style={{ background: COLORS[s.color].bg, borderColor: COLORS[s.color].stroke }}
                aria-hidden='true'
              />
              {s.label}
            </li>
          ))}
        </ul>
      </div>
    </div>
  )
}
